import { createFileRoute, redirect } from '@tanstack/react-router'
import { format, startOfDay, startOfMonth, subMonths } from 'date-fns'
import { parse } from 'valibot'

import { getAvailableDcsSelectQueryOptions } from '@/features/dcs/query-options'
import {
  fetchMonthlyTourReports,
  fetchMonthlyTourReportSummaries,
  type FetchMonthlyTourReportsQuery,
  type FetchMonthlyTourReportSummariesQuery
} from '@/features/monthly-tour-reports/api'
import { isDateRangeMode } from '@/features/shared/models'
import {
  selectedDcIdAtom,
  tenantStore
} from '@/routes/_protected/$tenantKey/-states'
import {
  MonthlyTourReportSearchValidateSchema,
  needsOperatingYearMonthDefault
} from '@/routes/_protected/$tenantKey/_performance_managements/monthly-tour-reports/-models'

export const Route = createFileRoute(
  '/_protected/$tenantKey/_performance_managements/monthly-tour-reports/'
)({
  validateSearch: (search) =>
    parse(MonthlyTourReportSearchValidateSchema, search),
  loaderDeps: ({ search }) => search,
  beforeLoad: async ({
    search,
    location,
    context: { auth, queryClient },
    params: { tenantKey }
  }) => {
    // TODO 権限チェック、他画面も

    const selectedDcId = tenantStore.get(selectedDcIdAtom)
    const availableDcs = await queryClient.ensureQueryData(
      getAvailableDcsSelectQueryOptions(tenantKey, auth.user)
    )
    const availableDcIds = availableDcs.map((x) => x.id)
    const filteredDcIds = search.dcIds?.filter((x) =>
      availableDcIds.includes(x)
    )

    // 運行年月 from/to が未指定の場合、from に前月・to に当月を初期値として設定する。
    // 拠点プルダウン切替で dcIds のみ送信され from/to が欠落するケース (#4128) もここで補完し、
    // BE で必須の運行年月が欠けたまま一覧 API を叩いて 400 になるのを防ぐ。
    // 日付範囲モードでは計上日の from/to が必須なので、未指定なら当月 1 日から本日を入れる (#4421)
    if (
      isDateRangeMode(search.searchMode) &&
      (!search.operatingDateFrom || !search.operatingDateTo)
    ) {
      const today = startOfDay(new Date())
      throw redirect({
        to: location.pathname,
        search: {
          ...search,
          operatingDateFrom:
            search.operatingDateFrom ??
            format(startOfMonth(today), 'yyyy-MM-dd'),
          operatingDateTo:
            search.operatingDateTo ?? format(today, 'yyyy-MM-dd'),
          dcIds:
            filteredDcIds && filteredDcIds.length > 0
              ? filteredDcIds
              : [selectedDcId]
        }
      })
    }

    if (
      !isDateRangeMode(search.searchMode) &&
      needsOperatingYearMonthDefault(search)
    ) {
      const today = startOfDay(new Date())
      const currentMonth = format(startOfMonth(today), 'yyyy-MM')
      const previousMonth = format(startOfMonth(subMonths(today, 1)), 'yyyy-MM')
      throw redirect({
        to: location.pathname,
        search: {
          ...search,
          operatingYearMonthFrom: previousMonth,
          operatingYearMonthTo: currentMonth,
          dcIds:
            filteredDcIds && filteredDcIds.length > 0
              ? filteredDcIds
              : [selectedDcId]
        }
      })
    }

    if (!filteredDcIds || filteredDcIds.length === 0) {
      throw redirect({
        to: location.pathname,
        search: {
          ...search,
          dcIds: [selectedDcId]
        }
      })
    }

    if (search.dcIds?.length !== filteredDcIds.length) {
      throw redirect({
        to: location.pathname,
        search: {
          ...search,
          dcIds: filteredDcIds
        }
      })
    }
  },
  loader: async ({
    abortController,
    params: { tenantKey },
    deps,
    context: { auth }
  }) => {
    // 日付範囲モードでは行の単位がパートナーに変わるため、別のエンドポイントを叩く (#4421)
    if (isDateRangeMode(deps.searchMode)) {
      const summaryQuery: FetchMonthlyTourReportSummariesQuery = {
        tenantKey,
        // beforeLoad で operatingDateFrom/To が未設定の場合はリダイレクトされるため、ここでは必ず値が存在する
        query: deps as FetchMonthlyTourReportSummariesQuery['query']
      }
      return {
        authUser: auth.user,
        tenantKey,
        monthlyTourReports: null,
        monthlyTourReportSummaries: await fetchMonthlyTourReportSummaries(
          summaryQuery,
          abortController.signal
        )
      }
    }

    const query: FetchMonthlyTourReportsQuery = {
      tenantKey,
      // beforeLoad で operatingYearMonthFrom/operatingYearMonthTo が未設定の場合はリダイレクトされるため、ここでは必ず値が存在する
      query: deps as FetchMonthlyTourReportsQuery['query']
    }
    return {
      authUser: auth.user,
      tenantKey,
      monthlyTourReports: await fetchMonthlyTourReports(
        query,
        abortController.signal
      ),
      monthlyTourReportSummaries: null
    }
  }
})
